import { useCallback, useState, type FormEvent } from 'react';
import { subscriptionGateway } from '../index';
import { SUPPORT_ISSUE_TYPES, type SupportIssueType, type SupportRequestCase } from '../billingTypes';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { EmptyState } from '../../../shared/EmptyState';
import { RetryableError } from '../../../shared/RetryableError';
import { useBilling } from './BillingContext';
import { billingErrorMessage, isPermissionDenied } from './billingErrors';
import { formatUtcDate } from './money';
import { useResource } from './useResource';

function issueLabel(value: string): string {
  return SUPPORT_ISSUE_TYPES.find((t) => t.value === value)?.label ?? value;
}

function statusLabel(status: string): string {
  switch (status) {
    case 'Open': return 'Open';
    case 'InProgress': return 'In progress';
    case 'Resolved': return 'Resolved';
    default: return status;
  }
}

/** Billing Support: a form that creates a trackable case, and the list of the company's cases with their status. The
 * team can also be reached at info@escochecks.com. */
export function SupportSection() {
  const { readOnly, markReadOnly } = useBilling();
  const [version, setVersion] = useState(0);
  const load = useCallback((signal: AbortSignal) => subscriptionGateway.listSupportRequests(signal), [version]); // eslint-disable-line react-hooks/exhaustive-deps
  const { state, reload } = useResource(load, 'Could not load your support cases.');

  const [issueType, setIssueType] = useState<SupportIssueType>('Invoice');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<SupportRequestCase | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!message.trim()) return;
    setBusy(true);
    setError(null);
    setCreated(null);
    try {
      const result = await subscriptionGateway.createSupportRequest({
        issueType,
        ...(invoiceNumber.trim() ? { invoiceNumber: invoiceNumber.trim() } : {}),
        message: message.trim(),
      });
      setCreated(result);
      setMessage('');
      setInvoiceNumber('');
      setVersion((n) => n + 1);
    } catch (err) {
      if (isPermissionDenied(err)) markReadOnly();
      else setError(billingErrorMessage(err, 'Could not send your request. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="billing-support-heading" className="billing-section" data-testid="billing-support">
      <h3 id="billing-support-heading">Billing support</h3>
      <p className="muted">Questions about an invoice, a payment, a refund or your plan? Send a request here and we will track it as a case. You can also email info@escochecks.com.</p>

      {created && (
        <p className="success" role="status" data-testid="support-created">
          Your request was received. Your case number is <strong>{created.caseNumber}</strong>.
        </p>
      )}

      {!readOnly ? (
        <form onSubmit={(e) => void submit(e)} className="billing-form" aria-label="Contact billing support">
          <label htmlFor="support-issue-type">What is this about?</label>
          <select id="support-issue-type" value={issueType} onChange={(e) => setIssueType(e.target.value as SupportIssueType)}>
            {SUPPORT_ISSUE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>

          <label htmlFor="support-invoice-number">Invoice number (optional)</label>
          <input id="support-invoice-number" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} autoComplete="off" />

          <label htmlFor="support-message">How can we help?</label>
          <textarea id="support-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={4} required />

          {error && <p className="error" role="alert">{error}</p>}
          <button type="submit" disabled={busy || !message.trim()}>{busy ? 'Sending…' : 'Send to billing support'}</button>
        </form>
      ) : (
        <p className="muted">Only a user with billing-management permission can send a billing support request. You can still see your company&apos;s cases below.</p>
      )}

      <h4>Your cases</h4>
      {state.status === 'loading' && <p className="muted" role="status">Loading your cases…</p>}
      {state.status === 'denied' && <AccessDeniedNotice requiredPermissionLabel="Billing: View" />}
      {state.status === 'unavailable' && <p className="muted">Billing is not available in this environment.</p>}
      {state.status === 'error' && <RetryableError message={state.message} onRetry={reload} />}
      {state.status === 'ready' && state.data.length === 0 && <EmptyState message="No support cases yet." />}
      {state.status === 'ready' && state.data.length > 0 && (
        <div className="billing-table-wrap">
          <table className="billing-table" aria-label="Billing support cases">
            <thead>
              <tr>
                <th scope="col">Case</th><th scope="col">About</th><th scope="col">Invoice</th><th scope="col">Status</th><th scope="col">Opened</th>
              </tr>
            </thead>
            <tbody>
              {state.data.map((c) => (
                <tr key={c.id}>
                  <th scope="row">{c.caseNumber}</th>
                  <td>{issueLabel(c.issueType)}</td>
                  <td>{c.invoiceNumber ?? '—'}</td>
                  <td>{statusLabel(c.status)}</td>
                  <td>{formatUtcDate(c.createdAtUtc)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
